/**
 * UsernameReminderBanner
 *
 * Dismissible top banner shown to existing users (Quick Start already completed)
 * whose username still matches their email, nudging them to personalize it via Profile.
 */
import React, { useEffect, useState } from "react";
import "../../styles/username-reminder-banner.css";

const DISMISS_KEY_PREFIX = "pista_username_reminder_dismissed_";

/**
 * @param {Object} props
 * @param {boolean} props.show - Whether the underlying condition (quick start completed
 *   and username === email) is currently true.
 * @param {number|string} [props.userId] - Current user's id, used to scope the
 *   per-session dismissal so it doesn't leak across accounts on a shared browser session.
 * @param {() => void} props.onOpenProfile - Called when the user clicks the CTA.
 */
function UsernameReminderBanner({ show, userId, onOpenProfile }) {
  const [dismissed, setDismissed] = useState(false);
  const dismissKey = `${DISMISS_KEY_PREFIX}${userId ?? "anon"}`;

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(dismissKey) === "1");
    } catch {
      // ignore storage errors
    }
  }, [dismissKey]);

  if (!show || dismissed) {
    return null;
  }

  const handleDismiss = (e) => {
    e.stopPropagation();
    try {
      sessionStorage.setItem(dismissKey, "1");
    } catch {
      // ignore storage errors
    }
    setDismissed(true);
  };

  return (
    <div className="username-reminder-banner" role="status">
      <span className="username-reminder-icon" aria-hidden="true">💡</span>
      <span className="username-reminder-message">
        Your username is the same as your email. Update it from your Profile for a more personalized experience.
      </span>
      <button
        type="button"
        className="username-reminder-cta"
        onClick={onOpenProfile}
      >
        Update Username
      </button>
      <button
        type="button"
        className="username-reminder-close"
        onClick={handleDismiss}
        aria-label="Dismiss username reminder"
      >
        ×
      </button>
    </div>
  );
}

export default UsernameReminderBanner;
